import { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../firebase-client';
import { ConfirmAccessModal } from '../components/modals/ConfirmAccessModal';
import {
  EXTERNAL_ATTENDEES_PAGE_SIZE,
  expandExternalAttendeeBookings,
  filterExternalAttendees,
} from './externalAttendeeRows';

const listAttendees = httpsCallable(functions, 'listExternalEventAttendees');
const getSafety = httpsCallable(functions, 'getExternalAttendeeSafety');

export default function ExternalEventAttendeesCard({ teamId }) {
  const [bookings, setBookings] = useState([]);
  const [clubSlug, setClubSlug] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [selectedRow, setSelectedRow] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [details, setDetails] = useState(null);
  const [copied, setCopied] = useState(false);
  const [search, setSearch] = useState('');
  const [eventFilter, setEventFilter] = useState('');
  const [page, setPage] = useState(1);

  const attendeeRows = useMemo(() => expandExternalAttendeeBookings(bookings), [bookings]);
  const eventNames = useMemo(() => Array.from(new Set(bookings.map(row => row.eventName).filter(Boolean))).sort(), [bookings]);
  const filteredRows = useMemo(() => filterExternalAttendees(attendeeRows, search, eventFilter), [attendeeRows, eventFilter, search]);
  const totalPages = Math.max(1, Math.ceil(filteredRows.length / EXTERNAL_ATTENDEES_PAGE_SIZE));
  const pageStart = (page - 1) * EXTERNAL_ATTENDEES_PAGE_SIZE;
  const visibleRows = filteredRows.slice(pageStart, pageStart + EXTERNAL_ATTENDEES_PAGE_SIZE);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError('');
    listAttendees({ teamId }).then(result => {
      if (!active) return;
      setBookings(result.data?.rows || []);
      setClubSlug(result.data?.clubSlug || '');
    }).catch(err => active && setLoadError(err?.message || 'Could not load external attendees.'))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [teamId]);

  const copyClubPage = async () => {
    if (!clubSlug || !navigator?.clipboard) return;
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/events/${clubSlug}`);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2200);
    } catch {
      setActionError('Could not copy the public events link.');
    }
  };

  const requestDetails = row => {
    if (!row.detailsAvailable) return;
    setSelectedRow(row);
    setActionError('');
    setConfirmOpen(true);
  };

  const confirmAndOpenDetails = async () => {
    if (!selectedRow) return;
    setDetailsLoading(true);
    setActionError('');
    try {
      const result = await getSafety({ teamId, eventId: selectedRow.eventRef, ticketId: selectedRow.bookingRef });
      setDetails({ title: selectedRow.eventName, focusName: selectedRow.attendeeName, ...result.data });
      setConfirmOpen(false);
    } catch (err) {
      setActionError(err?.message || 'Could not open attendee details.');
      setConfirmOpen(false);
    } finally {
      setDetailsLoading(false);
    }
  };

  const resultSummary = filteredRows.length === 0
    ? (search.trim() || eventFilter ? 'No external attendees match these filters.' : 'No external bookings yet.')
    : `${pageStart + 1}-${Math.min(pageStart + EXTERNAL_ATTENDEES_PAGE_SIZE, filteredRows.length)} of ${filteredRows.length} external attendee${filteredRows.length === 1 ? '' : 's'}`;

  return <div className="team-card external-attendees-card">
    <div className="card-header-with-icon"><span className="card-title">External Event Attendees ({attendeeRows.length})</span></div>
    <div className="card-body external-attendees-card__body">
      {clubSlug && <div className="external-attendees-card__share">
        <span>Public page</span>
        <div>
          <button type="button" className="external-attendees-card__share-action" onClick={copyClubPage} aria-label="Copy public events page link">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 7V5a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2M5 8h9a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-9a2 2 0 0 1 2-2Z" /></svg>
            <span>{copied ? 'Copied' : 'Copy'}</span>
          </button>
          <a className="external-attendees-card__share-action" href={`/events/${clubSlug}`} target="_blank" rel="noreferrer" aria-label="Open public events page">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M14 4h6v6M20 4l-9 9M18 13v6a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h6" /></svg>
            <span>Open</span>
          </a>
        </div>
      </div>}

      {!loading && !loadError && attendeeRows.length > 0 && <div className="external-attendees-card__filters">
        <input type="search" className="input" placeholder="Search attendees or bookings…" aria-label="Search external attendees" value={search} onChange={event => { setSearch(event.target.value); setPage(1); }} />
        <select className="input" aria-label="Filter external attendees by event" value={eventFilter} onChange={event => { setEventFilter(event.target.value); setPage(1); }}>
          <option value="">All events</option>
          {eventNames.map(name => <option key={name} value={name}>{name}</option>)}
        </select>
      </div>}

      {actionError && <p className="error-message" role="alert">{actionError}</p>}
      {loading ? <p>Loading external attendees…</p> : loadError ? <p className="error-message">{loadError}</p> : <>
        <div className="text-muted external-attendees-card__summary">{resultSummary}</div>
        <ul className="list-none divide-y external-attendees-card__list">
          {visibleRows.map(row => <li key={row.rowKey} className="external-attendees-card__row">
            <div className="external-attendees-card__identity">
              <strong>{row.attendeeName}</strong>
              <span>{row.eventName}{row.occurrenceId ? ` · ${row.occurrenceId}` : ''}</span>
              <small>Booking managed by {row.purchaserName}{row.purchaserIsTeamMember ? ' (club member)' : ''}</small>
            </div>
            <button
              type="button"
              className="btn btn--secondary btn--xs"
              disabled={!row.detailsAvailable}
              onClick={() => requestDetails(row)}
              aria-label={`View contact and safety details for ${row.attendeeName}`}
            >
              {row.detailsAvailable ? 'Contact & safety' : 'Details unavailable'}
            </button>
          </li>)}
        </ul>
        {filteredRows.length > EXTERNAL_ATTENDEES_PAGE_SIZE && <div className="external-attendees-card__pagination">
          <span className="text-muted">Page {page} of {totalPages}</span>
          <div>
            <button type="button" className="btn btn--secondary btn--xs" onClick={() => setPage(current => Math.max(1, current - 1))} disabled={page <= 1}>Prev</button>
            <button type="button" className="btn btn--secondary btn--xs" onClick={() => setPage(current => Math.min(totalPages, current + 1))} disabled={page >= totalPages}>Next</button>
          </div>
        </div>}
      </>}
    </div>

    {confirmOpen && selectedRow && <ConfirmAccessModal memberName={selectedRow.attendeeName} title="Access contact and safety details" detailLabel="protected contact and safety information" onCancel={() => { setConfirmOpen(false); setSelectedRow(null); }} onConfirm={confirmAndOpenDetails} busy={detailsLoading} />}

    {details && ReactDOM.createPortal(<div className="modal-backdrop" onClick={() => setDetails(null)}>
      <div className="modal-content external-attendee-details" role="dialog" aria-modal="true" aria-label={`Contact and safety details for ${details.focusName}`} onClick={event => event.stopPropagation()}>
        <h2 className="modal-title">Contact and safety details</h2>
        <p className="text-muted">{details.title}</p>
        {(details.attendees || []).map((attendee, index) => <section key={`${attendee.name}-${index}`} className="card external-attendee-details__person">
          <h3>{attendee.name || `Attendee ${index + 1}`}</h3>
          {attendee.phone && <p><span>Phone</span><a href={`tel:${attendee.phone}`}>{attendee.phone}</a></p>}
          {attendee.dob && <p><span>Date of birth</span><strong>{attendee.dob}</strong></p>}
          {(attendee.emergencyContacts || []).map((contact, contactIndex) => <div key={contactIndex} className="external-attendee-details__emergency">
            <strong>Emergency contact</strong>
            <span>{contact.name} · {contact.relationship}</span>
            <a href={`tel:${contact.phone}`}>{contact.phone}</a>
          </div>)}
        </section>)}
        {(details.attendees || []).length === 0 && <p>No external attendee details are available for this booking.</p>}
        <div className="form-actions"><button type="button" className="btn btn--primary" onClick={() => setDetails(null)}>Close</button></div>
      </div>
    </div>, document.body)}
  </div>;
}
